import BannerAI from "assets/png/corporate3.png";
import BannerFinance from "assets/png/corporate2.png";
import BannerSecurity from "assets/png/corporate4.png";

export interface Programme {
  slug: string;
  heroImage: string;
  category: string;
  deliveryMode: string;
  month: string;
  title: string;
  description: string;
  duration: string;
  price: number;
  certification: string;
  overview: string;
  whoShouldAttend?: string[];
  outline: string[];
}

// Source: CONCLASE MONTHLY TRAINING calendar (September – December 2026)
export const programmes: Programme[] = [
  // ── September 2026 ─────────────────────────────────────────────────────
  {
    slug: "microsoft-excel-for-banking-financial-analysis",
    heroImage: BannerFinance,
    category: "Data & Analytics",
    deliveryMode: "Virtual",
    month: "September 2026",
    title: "Microsoft Excel for Banking & Financial Analysis",
    description:
      "Master advanced Excel functions, pivot tables, dashboards and automation using practical banking and financial datasets.",
    duration: "2 days",
    price: 300000,
    certification: "Certificate of Completion",
    overview:
      "A hands-on programme built around practical banking and financial datasets. Participants move from data cleaning and pivot tables through XLOOKUP, INDEX/MATCH and financial calculations to management reports, dashboards and the automation of repetitive Excel tasks.",
    whoShouldAttend: [
      "Finance",
      "Operations",
      "Risk",
      "Audit",
      "Business Analysts",
      "Relationship Managers",
      "Other professionals working extensively with data",
    ],
    outline: [
      "Advanced Excel functions for business",
      "Data cleaning and preparation",
      "Pivot Tables and Pivot Charts",
      "XLOOKUP, INDEX/MATCH and logical functions",
      "Financial calculations and analysis",
      "Data validation and error checking",
      "Creating management reports",
      "Dashboards and visualisation",
      "Practical banking/financial datasets",
      "Automating repetitive Excel tasks",
    ],
  },
  {
    slug: "microsoft-power-bi-for-business-intelligence-reporting",
    heroImage: BannerFinance,
    category: "Data & Analytics",
    deliveryMode: "Virtual",
    month: "September 2026",
    title: "Microsoft Power BI for Business Intelligence & Reporting",
    description:
      "Turn organisational data into interactive dashboards and management reports with Power Query, data modelling and DAX.",
    duration: "3 days",
    price: 350000,
    certification: "Certificate of Completion",
    overview:
      "Learn to connect Power BI to Excel and other data sources, clean data with Power Query, build data models with relationships and calculated measures, and present KPI and performance dashboards for banking and financial services to management.",
    outline: [
      "Introduction to Business Intelligence",
      "Connecting Power BI to Excel and other data sources",
      "Data cleaning with Power Query",
      "Data modelling",
      "Relationships and calculated measures",
      "Introduction to DAX",
      "Creating interactive dashboards",
      "KPI and performance reporting",
      "Banking and financial services dashboards",
      "Sharing and presenting insights to management",
    ],
  },
  {
    slug: "cybersecurity-awareness-for-banking-financial-services",
    heroImage: BannerSecurity,
    category: "Security",
    deliveryMode: "Virtual",
    month: "September 2026",
    title: "Cybersecurity Awareness for Banking & Financial Services",
    description:
      "Understand the threats facing financial institutions and the everyday practices that keep customer and organisational data secure.",
    duration: "3 days",
    price: 300000,
    certification: "Certificate of Completion",
    overview:
      "Covers the cybersecurity landscape and the common threats facing financial institutions, from phishing and social engineering to insider threats, with practical guidance on passwords, data protection, secure email and internet use, mobile and remote-work security, and incident reporting.",
    outline: [
      "Understanding the cybersecurity landscape",
      "Common threats facing financial institutions",
      "Phishing and social engineering",
      "Password and access management",
      "Data protection and information security",
      "Secure email and internet practices",
      "Mobile and remote-work security",
      "Insider threats",
      "Incident reporting and response",
      "Cybersecurity best practices for employees",
    ],
  },

  // ── October 2026 ───────────────────────────────────────────────────────
  {
    slug: "generative-ai-chatgpt-for-banking-professionals",
    heroImage: BannerAI,
    category: "AI & Automation",
    deliveryMode: "Virtual",
    month: "October 2026",
    title: "Generative AI & ChatGPT for Banking Professionals",
    description:
      "Use ChatGPT and other generative AI tools for research, writing, analysis and customer service, responsibly and confidentially.",
    duration: "3 days",
    price: 200000,
    certification: "Certificate of Completion",
    overview:
      "An introduction to generative AI for banking professionals, covering effective prompting, AI-assisted research, reports, emails and presentations, data analysis, customer service and operations, and the responsible, ethical use of AI with confidential information.",
    whoShouldAttend: ["All banking professionals, not just technical staff"],
    outline: [
      "Introduction to Generative AI",
      "Understanding ChatGPT and other AI tools",
      "Effective prompting techniques",
      "AI for research and information gathering",
      "AI-assisted report and email writing",
      "Data analysis with AI",
      "Creating presentations and business documents",
      "AI for customer service and operations",
      "AI for productivity and workflow improvement",
      "Responsible and ethical use of AI",
      "Data privacy and confidential information",
    ],
  },
  {
    slug: "sql-for-business-financial-data-analysis",
    heroImage: BannerFinance,
    category: "Data & Analytics",
    deliveryMode: "Virtual",
    month: "October 2026",
    title: "SQL for Business & Financial Data Analysis",
    description:
      "Query databases with SQL to analyse customer and transaction data, spot trends and anomalies, and generate business reports.",
    duration: "3 days",
    price: 400000,
    certification: "Certificate of Completion",
    overview:
      "Starts with databases, tables and relationships and builds up to SELECT statements, filtering, joins, aggregation and grouping, then applies them to practical banking datasets to analyse customer and transaction data and generate reports.",
    whoShouldAttend: [
      "Business analysts",
      "Data analysts",
      "IT professionals",
      "Risk teams",
      "Operations and reporting teams",
    ],
    outline: [
      "Introduction to databases and SQL",
      "Understanding tables and relationships",
      "SELECT statements",
      "Filtering and sorting data",
      "Joins and combining datasets",
      "Aggregation and grouping",
      "Writing business queries",
      "Customer and transaction data analysis",
      "Identifying trends and anomalies",
      "Practical banking datasets",
      "Generating reports from databases",
    ],
  },
  {
    slug: "digital-transformation-automation-for-financial-services",
    heroImage: BannerAI,
    category: "AI & Automation",
    deliveryMode: "Virtual",
    month: "October 2026",
    title: "Digital Transformation & Automation for Financial Services",
    description:
      "Identify processes ready for automation and build a simple digital transformation roadmap for financial services.",
    duration: "2 days",
    price: 200000,
    certification: "Certificate of Completion",
    overview:
      "Explores digital banking technologies, process and workflow automation, an introduction to Robotic Process Automation (RPA) and AI-powered business processes, and how to measure automation ROI and reduce manual processes and errors.",
    outline: [
      "Understanding digital transformation",
      "Digital banking technologies",
      "Process automation",
      "Identifying processes suitable for automation",
      "Introduction to Robotic Process Automation (RPA)",
      "Workflow automation",
      "AI-powered business processes",
      "Digital customer experience",
      "Reducing manual processes and errors",
      "Measuring automation ROI",
      "Developing a simple digital transformation roadmap",
    ],
  },

  // ── November 2026 ──────────────────────────────────────────────────────
  {
    slug: "data-analytics-for-risk-fraud-financial-decision-making",
    heroImage: BannerFinance,
    category: "Data & Analytics",
    deliveryMode: "Virtual",
    month: "November 2026",
    title: "Data Analytics for Risk, Fraud & Financial Decision-Making",
    description:
      "Apply data analytics to risk, fraud indicators and transaction monitoring, with dashboards built in Excel and Power BI.",
    duration: "3 days",
    price: 300000,
    certification: "Certificate of Completion",
    overview:
      "Introduces financial data analytics and risk data, then covers fraud indicators and patterns, transaction monitoring, customer behaviour analysis and basic predictive analytics, using Excel and Power BI and financial-sector case studies.",
    outline: [
      "Introduction to financial data analytics",
      "Understanding risk data",
      "Fraud indicators and patterns",
      "Transaction monitoring",
      "Customer behaviour analysis",
      "Data-driven risk assessment",
      "Using Excel and Power BI for risk reporting",
      "Basic predictive analytics",
      "Dashboards for risk and fraud monitoring",
      "Case studies using financial-sector scenarios",
    ],
  },
  {
    slug: "cloud-computing-fundamentals-for-banking-financial-services",
    heroImage: BannerSecurity,
    category: "Cloud",
    deliveryMode: "Virtual",
    month: "November 2026",
    title: "Cloud Computing Fundamentals for Banking & Financial Services",
    description:
      "Understand cloud service models, security, migration and business continuity through practical banking use cases.",
    duration: "3 days",
    price: 200000,
    certification: "Certificate of Completion",
    overview:
      "Covers cloud computing fundamentals for financial services: IaaS, PaaS and SaaS, public, private and hybrid cloud, cloud security, data storage and backup, business continuity and disaster recovery, migration considerations and managing cloud risks.",
    outline: [
      "Introduction to cloud computing",
      "Cloud service models: IaaS, PaaS and SaaS",
      "Public, private and hybrid cloud",
      "Cloud in banking and financial services",
      "Cloud security",
      "Data storage and backup",
      "Business continuity and disaster recovery",
      "Cloud migration considerations",
      "Managing cloud risks",
      "Practical banking use cases",
    ],
  },
  {
    slug: "data-governance-privacy-protection-for-financial-institutions",
    heroImage: BannerSecurity,
    category: "Security",
    deliveryMode: "Virtual",
    month: "November 2026",
    title: "Data Governance, Privacy & Protection for Financial Institutions",
    description:
      "Build the governance, privacy and data-handling practices financial institutions need to manage data risk.",
    duration: "3 days",
    price: 300000,
    certification: "Certificate of Completion",
    overview:
      "Covers data ownership and accountability, data quality and integrity, classification, privacy principles, secure handling of customer information, access control and the data lifecycle, with governance frameworks and practical financial-services case studies.",
    outline: [
      "Understanding data governance",
      "Data ownership and accountability",
      "Data quality and integrity",
      "Data classification",
      "Data privacy principles",
      "Secure handling of customer information",
      "Access control",
      "Data lifecycle management",
      "Data governance frameworks",
      "Managing data risks",
      "Practical financial-services case studies",
    ],
  },

  // ── December 2026 ──────────────────────────────────────────────────────
  {
    slug: "ai-productivity-for-banking-professionals",
    heroImage: BannerAI,
    category: "AI & Automation",
    deliveryMode: "Virtual",
    month: "December 2026",
    title: "AI Productivity for Banking Professionals",
    description:
      "Learn to leverage AI tools to streamline banking workflows, automate repetitive tasks, and improve decision-making.",
    duration: "3 days",
    price: 300000,
    certification: "Certificate of Completion",
    overview:
      "This comprehensive program is designed specifically for banking professionals seeking to integrate Artificial Intelligence into their daily operations. Participants will explore practical applications of AI in risk assessment, customer service automation, and financial analysis, moving beyond theory to hands-on implementation that drives immediate business value.",
    whoShouldAttend: [
      "Bank Managers and Branch Leads",
      "Financial Analysts and Modelers",
      "Risk Assessment and Compliance Officers",
      "Customer Experience Leads in FinTech",
    ],
    outline: [
      "Introduction to AI-powered productivity",
      "ChatGPT and workplace AI tools",
      "Prompt engineering for professionals",
      "AI-assisted research",
      "Creating reports and presentations",
      "Analysing business data with AI",
      "Email and document automation",
      "Meeting summaries and action points",
      "Customer service applications",
      "AI for decision support",
      "Data privacy, security and responsible AI",
      "Building a personal AI productivity workflow",
    ],
  },
];

export const getProgrammeBySlug = (slug: string) =>
  programmes.find((p) => p.slug === slug);
